import { useEffect, useState, useRef } from "react"
import AdminLayout from "@/components/admin/AdminLayout"
// 💡 기존에 만들어두신 S3 API와 카테고리 API 임포트
import { getS3PresignedUrl, deleteS3Image } from "@/api/s3Api"
import {
    getAdminCategories, toggleMainCategory, toggleVisibleCategory, createAdminCategory, updateAdminCategory,
    updateCategorySort
} from "@/api/categoryApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {ChevronDown, ChevronUp, Plus, X} from "lucide-react"

export default function AdminCategoryPage() {
    const [categories, setCategories] = useState<any[]>([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [loading, setLoading] = useState(false)

    // 💡 상태 관리
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [formData, setFormData] = useState({ name: "", categoryCode: "", icon: "", description: "", isMain: "N", visibleYn: "Y", sortOrder: 0 })

    const fileInputRef = useRef<HTMLInputElement>(null)

    const fetchCategories = async () => {
        const res = await getAdminCategories()
        // setCategories(res?.data?.items || res?.items || [])

        // 💡 백엔드에서 받아온 후 sortOrder를 기준으로 정렬을 한 번 더 보장해줍니다.
        const list = res?.data?.items || res?.items || []
        const sortedList = list.sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))
        setCategories(sortedList)
    }
    useEffect(() => { fetchCategories() }, [])

    const handleToggleMain = async (id: number) => { await toggleMainCategory(id); fetchCategories(); }
    const handleToggleVisible = async (id: number) => { await toggleVisibleCategory(id); fetchCategories(); }

    const closeFormModal = () => {
        setIsModalOpen(false)
        setEditingId(null)
        setFormData({ name: "", categoryCode: "", icon: "", description: "", isMain: "N", visibleYn: "Y", sortOrder: 0 })
        setSelectedFile(null)
        setPreviewUrl(null)
        if (fileInputRef.current) fileInputRef.current.value = ""
    }

    const openCreateModal = () => { closeFormModal(); setIsModalOpen(true); }

    const openEditModal = (cat: any) => {
        setEditingId(cat.categoryId)
        setFormData({ ...cat, icon: cat.icon || "" })
        setPreviewUrl(cat.icon || null)
        setIsModalOpen(true)
    }

    // 💡 파일 선택 및 미리보기 생성
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null
        setSelectedFile(file)
        if (file) setPreviewUrl(URL.createObjectURL(file))
        else setPreviewUrl(formData.icon || null)
    }

    // 💡 파일 제거 기능
    const removeFile = () => {
        setSelectedFile(null)
        setPreviewUrl("")
        setFormData(prev => ({ ...prev, icon: "" }))
        if (fileInputRef.current) fileInputRef.current.value = ""
    }

    // 💡 핵심: S3 연동 업로드 및 DB 저장 로직
    const handleSave = async () => {
        if (!formData.name) return alert("카테고리명을 입력해주세요.")

        let finalIconUrl = formData.icon;


        try {
            setLoading(true)

            // @ts-ignore
            let newlyUploadedUrl; // 에러 발생 시 롤백용
            // 1. 선택된 새 파일이 있다면 프론트엔드에서 S3로 직접 업로드!
            if (selectedFile) {
                // 백엔드의 S3ApiController가 folder 파라미터를 지원하므로 카테고리 폴더로 요청해 봅니다. (지원 안되면 기본값 사용됨)
                // 만약 getS3PresignedUrl 파라미터가 1개뿐이라면 getS3PresignedUrl(selectedFile.name) 으로 사용하세요.
                const { presignedUrl, imageUrl } = await getS3PresignedUrl(selectedFile.name, "categories")

                // 직접 업로드 (fetch의 PUT 메소드 활용)
                await fetch(presignedUrl, {
                    method: "PUT",
                    body: selectedFile,
                    headers: { "Content-Type": selectedFile.type },
                })

                newlyUploadedUrl = imageUrl
                finalIconUrl = imageUrl
            }

            // 2. 최종 데이터를 만들어서 백엔드에 전송
            const payload = { ...formData, icon: finalIconUrl }

            if (editingId) {
                await updateAdminCategory(editingId, payload)
                alert("카테고리가 수정되었습니다.")
            } else {
                await createAdminCategory(payload)
                alert("신규 카테고리가 등록되었습니다.")
            }
            closeFormModal()
            fetchCategories()

        } catch (error) {
            console.error(error)
            // 💡 롤백: 방금 S3에 올렸던 이미지 삭제
            if (formData.icon !== finalIconUrl && finalIconUrl) {
                await deleteS3Image(finalIconUrl)
            }
            alert("저장에 실패했습니다.")
        } finally {
            setLoading(false)
        }
    }

    // 💡 신규: 순서 이동 핸들러
    const handleMoveOrder = async (index: number, direction: number) => {
        const newIndex = index + direction;
        // 배열 범위를 벗어나면 무시
        if (newIndex < 0 || newIndex >= categories.length) return;

        // 1. 기존 배열을 복사하고 두 요소의 위치를 스왑(Swap)합니다.
        const newCategories = [...categories];
        const temp = newCategories[index];
        newCategories[index] = newCategories[newIndex];
        newCategories[newIndex] = temp;

        // 2. 바뀐 배열을 기준으로 새로운 sortOrder (1부터 시작)를 매핑합니다.
        const sortData = newCategories.map((cat, idx) => ({
            categoryId: cat.categoryId,
            sortOrder: idx + 1
        }));

        // 3. UI를 즉시 업데이트하여 사용자에게 빠른 피드백 제공 (Optimistic UI)
        setCategories(newCategories);

        // 4. 백엔드에 바뀐 순서 일괄 저장
        try {
            await updateCategorySort(sortData);
        } catch (error) {
            alert("순서 변경에 실패했습니다.");
            fetchCategories(); // 실패 시 원래 데이터로 롤백
        }
    }
    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex items-end justify-between">
                    <div>
                        <h2 className="text-3xl font-bold">카테고리 관리</h2>
                        <p className="mt-2 text-sm text-slate-500">쇼핑몰의 카테고리 속성과 메인 화면 노출을 관리합니다.</p>
                    </div>
                    <Button className="rounded-xl bg-slate-900 hover:bg-slate-800" onClick={openCreateModal}>
                        신규 카테고리 등록
                    </Button>
                </div>

                <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                    <CardContent className="p-0">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                            <tr>
                                <th className="px-6 py-4 w-16">ID</th>
                                <th className="px-6 py-4 w-24 text-center">아이콘</th>
                                <th className="px-6 py-4 w-40">카테고리명</th>
                                <th className="px-6 py-4">코드</th>
                                <th className="px-6 py-4 w-28 text-center">사용 여부</th>
                                <th className="px-6 py-4 w-28 text-center">메인 노출</th>
                                <th className="px-6 py-4 w-24 text-center">관리</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                            {categories.map((cat, index) => (
                                <tr key={cat.categoryId} className={`hover:bg-slate-50 ${cat.visibleYn === 'N' ? 'opacity-50' : ''}`}>
                                    <td className="px-6 py-4">{cat.categoryId}</td>

                                    {/* 💡 S3 이미지를 예쁘게 렌더링 */}
                                    <td className="px-6 py-4 text-center">
                                        {cat.icon ? (
                                            <div className="h-10 w-10 mx-auto rounded-lg overflow-hidden border border-slate-200 bg-white">
                                                <img src={cat.icon} alt={cat.name} className="h-full w-full object-cover" />
                                            </div>
                                        ) : (
                                            <div className="h-10 w-10 mx-auto rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-xs">None</div>
                                        )}
                                    </td>

                                    <td className="px-6 py-4 font-bold text-slate-900">{cat.name}</td>
                                    <td className="px-6 py-4 text-slate-500">{cat.categoryCode}</td>
                                    <td className="px-6 py-4 text-center">
                                        <Button variant={cat.visibleYn === 'Y' ? 'default' : 'outline'} className={`rounded-full h-8 px-4 text-xs ${cat.visibleYn === 'Y' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-slate-500 bg-white'}`} onClick={() => handleToggleVisible(cat.categoryId)}>
                                            {cat.visibleYn === 'Y' ? '사용중' : '숨김'}
                                        </Button>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <Button variant={cat.isMain === 'Y' ? 'default' : 'outline'} disabled={cat.visibleYn === 'N'} className={`rounded-full h-8 px-4 text-xs ${cat.isMain === 'Y' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'text-slate-500 bg-white'}`} onClick={() => handleToggleMain(cat.categoryId)}>
                                            {cat.isMain === 'Y' ? '노출중' : '숨김'}
                                        </Button>
                                    </td>

                                    {/* 💡 신규: 순서 변경 화살표 UI */}
                                    <td className="px-4 py-4 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                            <button
                                                onClick={() => handleMoveOrder(index, -1)}
                                                disabled={index === 0} // 첫 번째 항목은 위로 불가
                                                className="p-1.5 rounded bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 disabled:opacity-30 disabled:hover:bg-slate-100 transition-colors"
                                            >
                                                <ChevronUp size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleMoveOrder(index, 1)}
                                                disabled={index === categories.length - 1} // 마지막 항목은 아래로 불가
                                                className="p-1.5 rounded bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900 disabled:opacity-30 disabled:hover:bg-slate-100 transition-colors"
                                            >
                                                <ChevronDown size={16} />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openEditModal(cat)}>
                                            수정
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            </div>

            {/* 등록/수정 모달 */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeFormModal}>
                    <div className="w-full max-w-md rounded-[28px] bg-white shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between p-6 border-b border-slate-100">
                            <h2 className="text-xl font-bold">{editingId ? "카테고리 수정" : "신규 카테고리 등록"}</h2>
                            <button className="text-slate-400 hover:text-slate-700 text-3xl font-light" onClick={closeFormModal}>&times;</button>
                        </div>

                        <div className="p-6 space-y-4">
                            {/* 💡 S3 이미지 업로드 UI (상품 관리 페이지 UI 통일) */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">카테고리 아이콘</label>
                                <div>
                                    {previewUrl ? (
                                        <div className="relative w-24 h-24 border border-slate-200 rounded-xl overflow-hidden group">
                                            <img src={previewUrl} alt="미리보기" className="w-full h-full object-cover bg-slate-50" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <button type="button" onClick={removeFile} className="bg-white text-red-500 rounded-full p-2 hover:bg-red-50 transition-colors shadow-sm">
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center justify-center w-24 h-24 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 hover:border-slate-400 transition-colors">
                                            <Plus className="text-slate-400 mb-1" size={24} />
                                            <span className="text-[10px] text-slate-500 font-medium">이미지 등록</span>
                                            <input type="file" accept="image/*" className="hidden" ref={fileInputRef} onChange={handleFileChange} />
                                        </label>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">카테고리명 *</label>
                                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="예: 식품" className="rounded-xl" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">카테고리 코드</label>
                                <Input value={formData.categoryCode} onChange={(e) => setFormData({...formData, categoryCode: e.target.value})} placeholder="예: FOOD" className="rounded-xl" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">카테고리 설명</label>
                                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="간단한 설명" className="rounded-xl" />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">정렬 순서</label>
                                    <Input type="number" value={formData.sortOrder} onChange={(e) => setFormData({...formData, sortOrder: Number(e.target.value)})} className="rounded-xl" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">사용 여부</label>
                                    <select value={formData.visibleYn} onChange={(e) => setFormData({...formData, visibleYn: e.target.value})} className="w-full h-10 rounded-xl border border-slate-200 px-3 outline-none">
                                        <option value="Y">사용 (노출)</option>
                                        <option value="N">미사용 (숨김)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 rounded-b-[28px]">
                            <Button variant="outline" className="rounded-xl" onClick={closeFormModal} disabled={loading}>취소</Button>
                            <Button className="rounded-xl bg-slate-900 hover:bg-slate-800" onClick={handleSave} disabled={loading}>
                                {loading ? "저장 중..." : (editingId ? "수정완료" : "등록하기")}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}